<template>
  <Modal
    :title="modalTitle"
    :mode="mode"
    :max-width="720"
    :body-overflow="mode === 'embedded' ? 'hidden' : 'auto'"
    :body-min-height="effectiveBodyMinHeight"
    :body-max-height="effectiveBodyMaxHeight"
    :embedded-footer="shouldShowFooter"
    @close="emit('close')"
  >
    <div v-if="loading && !reachoutData" class="muted">Loading reachout…</div>
    <div v-else-if="!reachoutData" class="muted">Reachout not found.</div>
    <div v-else :style="mode === 'embedded' ? 'display:flex; flex-direction:column; min-height:0; height:100%;' : 'display:flex; flex-direction:column; gap:16px;'">
      <div :style="mode === 'embedded' ? `display:flex; flex-direction:column; gap:16px; flex:1; min-height:0; overflow:${isEditing ? 'hidden' : 'auto'};` : 'display:flex; flex-direction:column; gap:16px;'">

        <!-- ── Phone Call Layout ── -->
        <template v-if="isCallType">
          <div class="addressBlock">
            <div class="fieldRow">
              <div class="fieldKey">To</div>
              <div class="toInlineBlock">
                <span class="toPrimaryText">{{ recipientContext?.name || '—' }}</span>
                <span v-if="recipientContext?.title" class="muted toSecondaryText">{{ recipientContext.title }}</span>
              </div>
            </div>
            <div class="fieldRow">
              <div class="fieldKey">Phone</div>
              <div style="flex:1; min-width:0;">
                <input v-if="isEditing" v-model="draftManualTo" class="input" placeholder="Phone number" :disabled="actionLoading" />
                <template v-else>
                  <div v-if="callPhoneNumbers.length" style="display:flex; flex-direction:column; gap:3px;">
                    <a v-for="num in callPhoneNumbers" :key="num" :href="`tel:${num}`" class="phoneLink">{{ num }}</a>
                  </div>
                  <span v-else class="muted missingInfo">No phone number on file</span>
                </template>
              </div>
            </div>
          </div>
          <div class="messageBlock" :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:hidden;' : ''">
            <div style="font-weight:600; line-height:1.3; color:var(--text); flex-shrink:0;">{{ reachoutData.subject || 'Phone call' }}</div>
            <div v-if="callResearchSummary && !isEditing" style="display:flex; flex-direction:column; gap:6px; flex-shrink:0;">
              <div class="fieldKey">Research &amp; hook</div>
              <div class="panel" style="white-space:pre-wrap; line-height:1.5; font-size:13px;">{{ callResearchSummary }}</div>
            </div>
            <div style="display:flex; flex-direction:column; gap:6px;" :style="isEditing && mode === 'embedded' ? 'min-height:0; flex:1; overflow:hidden;' : ''">
              <div class="fieldKey" style="flex-shrink:0;">Call script</div>
              <textarea v-if="isEditing" v-model="draftBody" class="input" :disabled="actionLoading" :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:auto; resize:vertical;' : 'min-height:180px;'"></textarea>
              <div v-else class="panel" :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:auto; white-space:pre-wrap; line-height:1.5;' : 'white-space:pre-wrap; line-height:1.5;'">{{ reachoutData.body || '' }}</div>
            </div>
          </div>
        </template>

        <!-- ── Voicemail Layout ── -->
        <template v-else-if="isVoicemailType">
          <div class="addressBlock">
            <!-- "From" intentionally omitted: the originating phone number is
                 chosen at dispatch time by the dialer's phone-pool claim, so
                 there's nothing meaningful to surface here. -->
            <div class="fieldRow">
              <div class="fieldKey">To</div>
              <div style="min-width:0; display:flex; flex-direction:column; gap:6px;">
                <template v-if="isEditing">
                  <input v-model="draftManualTo" class="input" placeholder="Phone number" :disabled="actionLoading" />
                </template>
                <template v-else>
                  <div class="toInlineBlock">
                    <span class="toPrimaryText" :title="voicemailToName">{{ voicemailToName }}</span>
                    <span v-if="toSecondaryLine" class="muted toSecondaryText" :title="toSecondaryLine">{{ toSecondaryLine }}</span>
                    <div v-if="recipientLinks.length" class="toLinkIcons">
                      <a v-for="link in recipientLinks" :key="link.label" :href="link.href" class="iconLink" target="_blank" rel="noopener" :title="link.label">
                        <FavIcon :domain="link.domain" :size="16" />
                      </a>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="!isEditing" class="fieldRow" :class="{ fieldRowOverride: showVoicemailDevOverride }">
              <div class="fieldKey" :class="{ fieldKeyOverride: showVoicemailDevOverride }">Phone</div>
              <div :class="showVoicemailDevOverride ? 'toOverrideLine' : ''">
                <span
                  class="toPrimaryText"
                  :class="{ toOverrideText: showVoicemailDevOverride }"
                  :title="voicemailDialedPhoneDisplay"
                >{{ voicemailDialedPhoneDisplay || '—' }}</span>
                <span v-if="showVoicemailDevOverride" class="toOverrideLabel">(development override)</span>
              </div>
            </div>

            <div v-if="!isEditing" class="fieldRow">
              <div class="fieldKey">Voice</div>
              <div>{{ voicemailVoiceName || '—' }}</div>
            </div>
          </div>
          <div class="messageBlock" :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:hidden;' : ''">
            <div style="font-weight:600; line-height:1.3; color:var(--text); flex-shrink:0;">Voicemail</div>
            <div style="display:flex; flex-direction:column; gap:6px;" :style="isEditing && mode === 'embedded' ? 'min-height:0; flex:1; overflow:hidden;' : ''">
              <div class="fieldKey" style="flex-shrink:0;">Voicemail script</div>
              <textarea v-if="isEditing" v-model="draftBody" class="input" :disabled="actionLoading" :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:auto; resize:vertical;' : 'min-height:120px;'"></textarea>
              <div v-else class="panel" :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:auto; white-space:pre-wrap; line-height:1.5;' : 'white-space:pre-wrap; line-height:1.5;'">{{ reachoutData.body || '' }}</div>
            </div>
            <div v-if="!isEditing && reachoutData.body" style="margin-top:8px;">
              <button class="btn" type="button" :disabled="voicemailSynthesizing" @click="listenVoicemailPreview">
                {{ voicemailSynthesizing ? 'Generating...' : 'Listen' }}
              </button>
            </div>
          </div>
        </template>

        <!-- ── LinkedIn Connect Layout ── -->
        <template v-else-if="isLinkedinType">
          <div class="addressBlock">
            <div class="fieldRow">
              <div class="fieldKey">To</div>
              <div class="toInlineBlock">
                <span class="toPrimaryText">{{ recipientContext?.name || '—' }}</span>
                <span v-if="recipientContext?.title" class="muted toSecondaryText">{{ recipientContext.title }}</span>
              </div>
            </div>
            <div class="fieldRow">
              <div class="fieldKey">LI</div>
              <div style="flex:1; min-width:0;">
                <input v-if="isEditing" v-model="draftManualTo" class="input" placeholder="LinkedIn profile URL" :disabled="actionLoading" />
                <template v-else>
                  <a v-if="linkedinUrl && linkedinUrl !== 'unknown'" :href="linkedinUrl" target="_blank" rel="noopener" class="liLink">
                    <FavIcon domain="linkedin.com" :size="14" style="flex-shrink:0;" />
                    <span style="word-break:break-all;">{{ linkedinUrl }}</span>
                  </a>
                  <span v-else class="muted missingInfo">No LinkedIn URL on file</span>
                </template>
              </div>
            </div>
          </div>
          <div class="messageBlock" :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:hidden;' : ''">
            <div style="display:flex; flex-direction:column; gap:6px;" :style="isEditing && mode === 'embedded' ? 'min-height:0; flex:1; overflow:hidden;' : ''">
              <div style="display:flex; align-items:center; justify-content:space-between; gap:8px; flex-shrink:0;">
                <div class="fieldKey">Connection note</div>
                <span v-if="!isEditing" class="charCounter" :class="{ charCounterWarn: linkedinCharCount > 450 }">{{ linkedinCharCount }}/500</span>
                <span v-if="isEditing" class="charCounter" :class="{ charCounterWarn: draftBody.length > 450 }">{{ draftBody.length }}/500</span>
              </div>
              <textarea v-if="isEditing" v-model="draftBody" class="input" :disabled="actionLoading" :maxlength="500" :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:auto; resize:vertical;' : 'min-height:120px;'"></textarea>
              <div v-else class="panel liMessagePanel" :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:auto; white-space:pre-wrap; line-height:1.5;' : 'white-space:pre-wrap; line-height:1.5;'">{{ reachoutData.body || '' }}</div>
            </div>
          </div>
        </template>

        <!-- ── Email Layout (default) ── -->
        <template v-else>
          <div class="addressBlock">
            <div class="fieldRow">
              <div class="fieldKey">From</div>
              <div style="min-width:0;">
                <SearchSelect
                  v-if="isEditing"
                  v-model="draftFromIdentity"
                  :options="senderIdentitySelectOptions"
                  placeholder="Select sender..."
                  search-placeholder="Search senders..."
                  :disabled="actionLoading"
                />
                <div v-else style="font-weight:600; line-height:1.4; display:flex; align-items:center; gap:6px;">
                  {{ fromDisplay || '—' }}
                  <FavIcon v-if="fromProvider" :domain="fromProvider === 'gmail' ? 'google.com' : 'sendgrid.com'" :size="14" />
                </div>
              </div>
            </div>

            <div class="fieldRow">
              <div class="fieldKey">To</div>
              <div style="min-width:0; display:flex; flex-direction:column; gap:6px;">
                <template v-if="isEditing">
                  <div v-if="hasLinkedContact" class="contactPickerRow">
                    <div class="contactPickerSelect">
                      <SearchSelect
                        v-model="draftContactId"
                        :options="contactOptions"
                        placeholder="Select a contact..."
                        search-placeholder="Search contacts..."
                        :disabled="actionLoading || contactsLoading || !reachoutAccountId"
                      />
                    </div>
                    <button
                      class="iconBtn"
                      type="button"
                      title="Create contact"
                      aria-label="Create contact"
                      :disabled="actionLoading || !reachoutAccountId"
                      @click="openCreateContact"
                    >
                      <AppIcon name="plus" :size="16" />
                    </button>
                    <button
                      class="iconBtn"
                      type="button"
                      title="Edit contact"
                      aria-label="Edit contact"
                      :disabled="actionLoading || !draftContactId"
                      @click="openEditContact"
                    >
                      <AppIcon name="pencil" :size="16" />
                    </button>
                  </div>
                  <input v-else v-model="draftTo" class="input" :disabled="actionLoading" />
                </template>
                <template v-else>
                  <div class="toInlineBlock">
                    <VerificationMark data-type="email" :status="recipientContext?.leadiq_status" />
                    <span class="toPrimaryText" :title="toPrimaryLine">{{ toPrimaryLine }}</span>
                    <DataProvenance
                      :provider="recipientContext?.email_provider"
                      data-type="email"
                      :data-value="recipientContext?.email"
                    />
                    <span v-if="toSecondaryLine" class="muted toSecondaryText" :title="toSecondaryLine">{{ toSecondaryLine }}</span>
                    <div v-if="recipientLinks.length" class="toLinkIcons">
                      <a v-for="link in recipientLinks" :key="link.label" :href="link.href" class="iconLink" target="_blank" rel="noopener" :title="link.label">
                        <FavIcon :domain="link.domain" :size="16" />
                      </a>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="showDevOverride && !isEditing" class="fieldRow fieldRowOverride">
              <div class="fieldKey fieldKeyOverride">To</div>
              <div class="toOverrideLine">
                <span class="toPrimaryText toOverrideText" :title="actualRecipient">{{ actualRecipient }}</span>
                <span class="toOverrideLabel">(development override)</span>
              </div>
            </div>
          </div>

          <div class="messageBlock" :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:hidden;' : ''">
            <!-- Subject and body deliberately share one shape between view and
                 edit: bare text separated by rules, no field boxes. Editing then
                 changes what you can do, not where anything sits. -->
            <input
              v-if="isEditing"
              v-model="draftSubject"
              class="bareSubject"
              placeholder="Subject"
              :disabled="actionLoading"
            />
            <div v-else class="bareSubject">{{ reachoutData.subject || '(no subject)' }}</div>

            <div class="bareRule"></div>

            <textarea
              v-if="isEditing"
              v-model="draftBody"
              class="bareBody"
              placeholder="Write your message…"
              :disabled="actionLoading"
              :style="mode === 'embedded' ? 'min-height:0; flex:1;' : 'min-height:180px;'"
            ></textarea>
            <template v-else>
              <div v-if="hasAnnotatedBlocks" class="bodyModeBar">
                <button class="modeBtn" :class="{ active: bodyViewMode === 'plain' }" @click="bodyViewMode = 'plain'">Plain</button>
                <button class="modeBtn" :class="{ active: bodyViewMode === 'annotated' }" @click="bodyViewMode = 'annotated'">Annotated</button>
              </div>

              <div
                v-if="bodyViewMode === 'plain' || !hasAnnotatedBlocks"
                  class="bareBody bareBodyView"
                  :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:auto;' : ''"
                >{{ displayBody }}</div>

              <div v-else class="annotatedLayout" :class="{ embedded: mode === 'embedded' }">
                <div
                  class="annotatedBodyPanel panel"
                  :style="mode === 'embedded' ? 'min-height:0; flex:1; overflow:visible; white-space:pre-wrap; line-height:1.5;' : 'overflow:visible; white-space:pre-wrap; line-height:1.5;'"
                >
                  <span
                    v-for="block in annotatedBlocks"
                    :key="block.block_id || block.text"
                    class="annotatedInlineWrap"
                    @pointerenter="hoverAnnotatedBlock(block)"
                    @pointermove="hoverAnnotatedBlock(block)"
                    @pointerleave="clearHoveredAnnotatedBlock($event)"
                  >
                    <span
                      class="annotatedInlineBlock"
                      :class="{
                        interactive: Array.isArray(block.data_points) && block.data_points.length > 0,
                        hovered: String(hoveredAnnotatedBlock?.block_id || '') === String(block.block_id || '')
                      }"
                    >{{ block.text || '' }}</span>
                    <div
                      v-if="String(hoveredAnnotatedBlock?.block_id || '') === String(block.block_id || '') && annotatedBlockDataPoints(block).length"
                      class="annotatedHovercard"
                      @pointerenter="hoverAnnotatedBlock(block)"
                      @pointermove="hoverAnnotatedBlock(block)"
                      @pointerleave="clearHoveredAnnotatedBlock($event)"
                    >
                      <div class="annotatedHovercardTitle">Data points</div>
                      <DataPointRows
                        :data-points="annotatedBlockDataPoints(block)"
                        compact
                        @select="openDataPointDetail"
                      />
                    </div>
                  </span>
                  <div v-if="replyQuotePlain" style="white-space:pre-wrap; color:var(--muted); margin-top:16px;">{{ replyQuotePlain }}</div>
                </div>
              </div>
            </template>

          </div>

        </template>

      </div>
      <div v-if="showMetaPills" class="pill-row start" style="padding-top:8px; flex-shrink:0;">
        <StatusPill :status="pillStatus" :label="needsMailbox ? 'Needs a mailbox' : ''" />
        <span v-if="isStarred" class="badge" title="Starred as exemplary">
          <AppIcon name="star_filled" :size="16" style="color: #f59e0b;" />
          <span>Starred</span>
        </span>
        <router-link v-if="agentRunHref" :to="agentRunHref" class="badge pill-link" :title="agentPillLabel">
          <HeroIcon
            v-if="agentPillIconName"
            :name="agentPillIconName"
            :iconStyle="agentPillIconStyle || 'outline'"
            :color="agentPillIconColor || 'var(--muted)'"
            :size="16"
          />
          <AppIcon v-else name="agent" :size="16" />
          <span>{{ agentPillLabel }}</span>
        </router-link>
        <router-link v-if="accountHref" :to="accountHref" class="badge pill-link" :title="accountPillLabel">
          <FavIcon :domain="reachoutData.account_domain || ''" :size="16" />
          <span>{{ accountPillLabel }}</span>
        </router-link>
        <router-link
          v-if="sequenceName && sequenceHref"
          :to="sequenceHref"
          class="badge pill-link"
          :title="`Sequence: ${sequenceName}`"
        >
          <AppIcon name="sequence" :size="16" />
          <span>{{ sequenceName }}</span>
        </router-link>
        <span v-else-if="sequenceName" class="badge" :title="`Sequence: ${sequenceName}`">
          <AppIcon name="sequence" :size="16" />
          <span>{{ sequenceName }}</span>
        </span>
        <span v-if="sequenceStepLabel" class="badge">
          <HeroIcon name="ListBulletIcon" iconStyle="outline" :size="16" />
          <span>{{ sequenceStepLabel }}</span>
        </span>
        <template v-if="crmSync">
          <a
            v-if="crmSync.href"
            class="badge pill-link"
            :href="crmSync.href"
            target="_blank"
            rel="noopener"
            :title="`Open ${crmSync.label} activity`"
          >
            <FavIcon :domain="crmSync.domain" :size="16" />
            <span>{{ crmSync.label }}</span>
          </a>
          <span v-else class="badge" :title="`Synced to ${crmSync.label}`">
            <FavIcon :domain="crmSync.domain" :size="16" />
            <span>{{ crmSync.label }}</span>
          </span>
        </template>
        <a
          v-if="reachoutData.slack_permalink"
          class="badge pill-link"
          :href="reachoutData.slack_permalink"
          target="_blank"
          rel="noopener"
          title="Open Slack notification"
        >
          <FavIcon domain="slack.com" :size="16" />
          <span>Slack</span>
        </a>
        <span
          v-else-if="reachoutData.payload?.slack_notification?.ok"
          class="badge"
          title="Slack notification sent (no link available)"
        >
          <FavIcon domain="slack.com" :size="16" />
          <span>Slack</span>
        </span>
        <button
          v-if="hasRevisions"
          type="button"
          class="badge revisionPill"
          title="View revision history"
          @click="openRevisionDialog"
        >
          <HeroIcon name="DocumentDuplicateIcon" iconStyle="outline" :size="16" />
          <span>Revisions</span>
          <span v-if="revisionCount" class="pillCount">{{ revisionCount }}</span>
        </button>
        <button
          v-if="showDeliveryEventsPill"
          type="button"
          class="badge revisionPill"
          :title="`View ${deliveryEventsLabel.toLowerCase()}`"
          @click="deliveryDialogOpen = true"
        >
          <HeroIcon name="TruckIcon" iconStyle="outline" :size="16" />
          <span>{{ deliveryEventsLabel }}</span>
          <span v-if="deliveryEvents.length" class="pillCount">{{ deliveryEvents.length }}</span>
        </button>
        <!--
          Email-pipeline scheduling badge ("Scheduled · {tz}" / "Send immediately").
          Suppressed for voicemail reachouts: their scheduling is enforced by the
          dialer on the dispatch row (the prospect's local calling hours),
          not by the reachout's send-timezone fields. The
          per-dispatch timing is surfaced via the lifecycle_state pill instead
          (scheduled → dialing).
        -->
        <span
          v-if="!isVoicemailType && (status === 'scheduled_for_sending' || status === 'pending_approval' || (isPostSent && reachoutData?.sent_timezone_source != null))"
          class="badge"
          :title="sendTimezoneTitle"
        >
          <AppIcon :name="timezoneBadgeMode === 'scheduled' ? 'timeline' : 'arrow-right-circle'" :size="16" />
          <span>{{ timezoneBadgeLabel }}</span>
        </span>
      </div>

      <!-- Revision in-flight banner -->
      <div v-if="status === 'revision_in_progress' && activeRevisionRunId" class="revisionBanner">
        <span class="spinner" />
        <span>Revision in progress</span>
        <router-link :to="`/activity/${activeRevisionRunId}`" class="revisionRunLink">View run →</router-link>
      </div>

    </div>

    <ReachoutModal
      v-if="selectedKind === 'reachout' && selectedReachout"
      :reachout="selectedReachout"
      @close="closeDetail"
    />
    <SalesforceEmailModal
      v-if="selectedKind === 'salesforce_email' && selectedSalesforceEmailId && reachoutAccountId"
      :account-id="reachoutAccountId"
      :email-id="selectedSalesforceEmailId"
      @close="closeDetail"
    />
    <SalesforceEventModal
      v-if="selectedKind === 'salesforce_event' && selectedSalesforceEventId && reachoutAccountId"
      :account-id="reachoutAccountId"
      :event-id="selectedSalesforceEventId"
      @close="closeDetail"
    />
    <SignalDetailModal
      v-if="selectedKind === 'signal' && selectedSignal"
      :signal="selectedSignal"
      :account-name-by-domain="{}"
      :account-id-by-domain="{}"
      :agent-by-id="{}"
      @close="closeDetail"
    />
    <ObjectDetailModal
      v-if="selectedKind === 'object' && selectedObjectType && selectedObjectId"
      :object-type="selectedObjectType"
      :object-id="selectedObjectId"
      :account-id="reachoutAccountId"
      @close="closeDetail"
    />

    <ContactEditorModal
      v-if="showContactEditor && reachoutAccountId"
      :account-id="reachoutAccountId"
      :contact="editingContact"
      @close="closeContactEditor"
      @saved="handleContactSaved"
    />

    <RevisionPanel
      v-if="showRevisePanel && reachoutData"
      :reachoutIds="[String(reachoutData.id)]"
      @close="showRevisePanel = false"
      @revised="handleRevisionComplete"
    />

    <template v-if="shouldShowFooter" #footer>
      <div class="modalFooterShell">
        <div v-if="actionError" class="error" style="margin:0;">{{ actionError }}</div>
        <div v-else-if="canApprove && approvalBlocked" class="approvalBlockNote">{{ approvalBlockReason }}</div>
        <div v-else></div>
        <div class="modalFooterRow">
          <div class="modalFooterDanger">
            <button v-if="!isEditing && canDelete" class="btn danger" :disabled="actionLoading || !userCanAct" @click="openDismissDialog">Delete…</button>
          </div>
          <div class="modalFooterActions">
            <button v-if="isEditing" class="btn" :disabled="actionLoading" @click="discardEdits">Discard</button>
            <button v-if="isEditing" class="btn primary" :disabled="actionLoading" @click="saveDraft">{{ actionLoading ? 'Saving…' : 'Save Changes' }}</button>
            <button v-if="!isEditing" class="btn" :disabled="actionLoading || !userCanAct" @click="toggleStar" :title="isStarred ? 'Unstar' : 'Star as exemplary'">
              <AppIcon :name="isStarred ? 'star_filled' : 'star'" :size="16" :style="isStarred ? 'color: #f59e0b;' : ''" />
            </button>
            <button
              v-if="!isEditing && canEdit"
              class="btn"
              :disabled="actionLoading || !userCanAct || !canRevise"
              :title="!canRevise ? 'Only reachouts awaiting approval can be revised' : undefined"
              @click="showRevisePanel = true"
            >
              <AppIcon name="reachout" :size="16" />
              <span>Revise</span>
            </button>
            <button v-if="!isEditing && canEdit" class="btn" :disabled="actionLoading || !userCanAct" @click="beginEdit">
              <AppIcon name="email_setting" :size="16" />
              <span>Edit</span>
            </button>
            <button
              v-if="!isEditing && needsMailbox && GOOGLE_INTEGRATION_URL"
              type="button"
              class="btn primary"
              data-action="connect-mailbox"
              :disabled="connectingGmail"
              @click="connectGmail"
            >
              <AppIcon name="reachout" :size="16" />
              <span>Connect a mailbox to send</span>
            </button>
            <router-link
              v-else-if="!isEditing && needsMailbox"
              to="/integrations?open=google-workspace"
              class="btn primary"
              data-action="connect-mailbox"
            >
              <AppIcon name="reachout" :size="16" />
              <span>Connect a mailbox to send</span>
            </router-link>
            <button
              v-if="!isEditing && canApprove"
              type="button"
              class="btn primary"
              :disabled="actionLoading || !userCanAct || approvalBlocked"
              :title="approvalBlocked ? approvalBlockReason : undefined"
              @click="approve"
            >
              <AppIcon name="reachout" :size="16" />
              <span>{{ actionLoading ? 'Sending…' : 'Approve & Send' }}</span>
            </button>
            <div v-if="!isEditing && canUpdateStatus" class="updateStatusGroup">
              <select
                class="input updateStatusSelect"
                v-model="selectedUpdateStatus"
                :disabled="actionLoading || !userCanAct"
              >
                <option value="" disabled>Update Status…</option>
                <option v-for="opt in updateStatusOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
              <button
                class="btn primary"
                :disabled="actionLoading || !selectedUpdateStatus || !userCanAct"
                @click="submitUpdateStatus"
              >{{ actionLoading ? 'Saving…' : 'Apply' }}</button>
            </div>
            <button v-if="mode === 'modal'" class="btn primary" :disabled="actionLoading" @click="emit('close')">Close</button>
          </div>
        </div>
      </div>
    </template>
  </Modal>

  <!-- Dismiss dialog -->
  <div v-if="dismissDialogOpen" class="dismissOverlay" @click.self="closeDismissDialog">
    <div class="dismissDialog" role="dialog" aria-modal="true" aria-labelledby="dismissTitle">
      <div class="dismissHeader">
        <span id="dismissTitle" class="dismissTitle">Delete...</span>
        <button class="dismissClose" aria-label="Close" @click="closeDismissDialog">✕</button>
      </div>
      <div class="dismissBody">
        <p class="dismissHint">After deletion, this sequence should...</p>
        <label v-for="opt in DISMISS_OPTIONS" :key="opt.value" class="dismissOption">
          <input type="radio" name="dismissReason" :value="opt.value" v-model="dismissReason" />
          <span>{{ opt.label }}</span>
        </label>
        <textarea
          v-model="dismissNotes"
          class="input dismissNotes"
          placeholder="Notes (optional)"
          rows="3"
        />
      </div>
      <div class="dismissFooter">
        <button class="btn" :disabled="actionLoading" @click="closeDismissDialog">Cancel</button>
        <button class="btn danger" :disabled="actionLoading || !dismissReason" @click="confirmDismiss">
          {{ actionLoading ? 'Deleting…' : 'Delete' }}
        </button>
      </div>
    </div>
  </div>

  <div v-if="revisionDialogOpen" class="dismissOverlay" @click.self="closeRevisionDialog">
    <div class="revisionDialog" role="dialog" aria-modal="true" aria-labelledby="revisionTitle">
      <div class="dismissHeader">
        <span id="revisionTitle" class="dismissTitle">Revision History</span>
        <button class="dismissClose" aria-label="Close" @click="closeRevisionDialog">✕</button>
      </div>
      <div class="revisionDialogBody">
        <div v-if="!revisionHistoryLoaded" class="muted">Loading…</div>
        <div v-else-if="revisionHistory.length === 0" class="muted">No revisions yet.</div>
        <div v-for="rev in revisionHistory" :key="rev.id" class="revisionEntry">
          <div class="revisionEntryHeader">
            <span class="revisionDate">{{ rev.created_at ? formatDateTimeLong(rev.created_at) : '' }}</span>
            <span class="badge">{{ revisionKindLabel(rev) }}</span>
            <span v-if="rev.actor_name" class="badge">{{ rev.actor_name }}</span>
            <span v-if="rev.instructions" class="revisionInstr">{{ rev.instructions }}</span>
          </div>
          <template v-if="rev.status === 'completed'">
            <LineDiff
              v-if="rev.previous_subject !== rev.revised_subject"
              :before="rev.previous_subject || ''"
              :after="rev.revised_subject || ''"
              leftLabel="Subject before"
              rightLabel="Subject after"
            />
            <LineDiff
              v-if="rev.previous_body !== rev.revised_body"
              :before="rev.previous_body || ''"
              :after="rev.revised_body || ''"
              leftLabel="Before"
              rightLabel="After"
            />
            <div v-if="status === 'pending_approval'" class="revertRow">
              <button class="btn" :disabled="!userCanAct" @click="revertRevision(rev.id)">Revert to this version</button>
            </div>
          </template>
          <div v-else-if="rev.status === 'error'" class="revisionError">Error: {{ rev.error }}</div>
          <div v-else-if="rev.status === 'aborted'" class="revisionAborted">Aborted (reachout was sent)</div>
        </div>
      </div>
    </div>
  </div>

  <div v-if="deliveryDialogOpen" class="dismissOverlay" @click.self="deliveryDialogOpen = false">
    <div class="deliveryDialog" role="dialog" aria-modal="true" aria-labelledby="deliveryTitle">
      <div class="dismissHeader">
        <span id="deliveryTitle" class="dismissTitle">{{ deliveryEventsLabel }}</span>
        <button class="dismissClose" aria-label="Close" @click="deliveryDialogOpen = false">✕</button>
      </div>
      <div class="revisionDialogBody">
        <div v-if="eventsLoading" class="muted">Loading events…</div>
        <div v-else-if="!deliveryEvents.length" class="muted">No events yet.</div>
        <div v-else class="eventList">
          <div v-for="ev in deliveryEvents" :key="ev.id" class="eventRow">
            <span class="badge">{{ ev.event_type }}</span>
            <span class="muted">{{ formatDateTimeLong(ev.occurred_at) }}</span>
            <span v-if="eventContext(ev)" class="muted">{{ eventContext(ev) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppIcon from './AppIcon.vue'
import DataPointRows from './DataPointRows.vue'
import SalesforceEmailModal from './SalesforceEmailModal.vue'
import SalesforceEventModal from './SalesforceEventModal.vue'
import SignalDetailModal from './SignalDetailModal.vue'
import ObjectDetailModal from './ObjectDetailModal.vue'
import HeroIcon from './HeroIcon.vue'
import LineDiff from './LineDiff.vue'
import RevisionPanel from './RevisionPanel.vue'
import Modal from './Modal.vue'
import DataProvenance from './DataProvenance.vue'
import VerificationMark from './VerificationMark.vue'
import FavIcon from './FavIcon.vue'
import SearchSelect from './SearchSelect.vue'
import StatusPill from './StatusPill.vue'
import { apiFetch } from '../services/api.ts'
import { fetchReachout } from '../services/outcomeItems.ts'
import { GOOGLE_INTEGRATION_URL } from '../env.ts'
import { useGoogleWorkspaceIntegration } from '../hooks/useGoogleWorkspaceIntegration.ts'
import ContactEditorModal from './ContactEditorModal.vue'
import { getSalesforceUrl } from '../services/salesforce.ts'
import { hubspotCallReviewUrl, preloadCrmLinkConfig, salesforceTaskReviewUrl } from '../services/crmLinks.ts'

const props = defineProps({
  reachout: { type: Object, required: true },
  mode: { type: String, default: 'modal' },
  bodyMinHeight: { type: [String, Number], default: '' },
  bodyMaxHeight: { type: [String, Number], default: '' },
})

const emit = defineEmits(['close', 'updated', 'deleted'])

const reachoutData = ref(props.reachout || null)
const loading = ref(false)
let loadGeneration = 0
const error = ref('')
const actionError = ref('')
const actionLoading = ref(false)

const revisionHistory = ref([])

const revisionHistoryLoaded = ref(false)
const revisionDialogOpen = ref(false)

const revisionCount = computed(() =>
  revisionHistoryLoaded.value ? revisionHistory.value.length : (reachoutData.value?.revision_count || 0),
)

function revisionKindLabel(rev) {
  if (rev.edited_by_user_id) return 'Direct edit'
  return rev.depth ? `${rev.depth} revision` : 'Revision'
}

function openRevisionDialog() {
  revisionDialogOpen.value = true
  if (!revisionHistoryLoaded.value) loadRevisionHistory()
}

function closeRevisionDialog() { revisionDialogOpen.value = false }

async function loadRevisionHistory() {
  const rid = reachoutData.value?.id
  if (!rid) return
  try {
    const resp = await apiFetch(`/reachouts/${rid}/revisions`)
    revisionHistory.value = resp?.revisions || []
  } catch {
    revisionHistory.value = []
  }
  revisionHistoryLoaded.value = true
}

async function revertRevision(revisionId) {
  const rid = reachoutData.value?.id
  if (!rid) return
  actionLoading.value = true
  try {
    await apiFetch(`/reachouts/${rid}/revisions/${revisionId}/revert`, { method: 'POST' })
    // Reload the reachout to reflect reverted content
    const resp = await fetchReachout(rid)
    if (resp) {
      reachoutData.value = resp
      emit('updated', resp)
    }
    await loadRevisionHistory()
  } catch (e) {
    actionError.value = e?.message || 'Revert failed'
  } finally {
    actionLoading.value = false
  }
}

// Reset revision history when navigating to a different reachout
watch(() => reachoutData.value?.id, () => {
  revisionHistory.value = []
  revisionHistoryLoaded.value = false
})

const showRevisePanel = ref(false)

async function handleRevisionComplete() {
  showRevisePanel.value = false
  const rid = reachoutData.value?.id
  if (rid) {
    const resp = await fetchReachout(rid)
    if (resp) {
      reachoutData.value = resp
      emit('updated', resp)
    }
    await loadRevisionHistory()
  }
}

const dismissDialogOpen = ref(false)
const dismissReason = ref('')
const dismissNotes = ref('')

const DISMISS_OPTIONS = [
  { value: 'skip',            label: 'Skip to next step' },
  { value: 'engaged',         label: 'Exit sequence — engaged' },
  { value: 'disqualified',    label: 'Exit sequence — disqualified' },
  { value: 'bad_icp_fit',     label: 'Exit sequence — bad ICP fit for account' },
  { value: 'bad_persona_fit', label: 'Exit sequence — bad persona fit for prospect' },
]
const emailEvents = ref([])
const voicemailEvents = ref([])
const eventsLoading = ref(false)
const deliveryDialogOpen = ref(false)
const isEditing = ref(false)
const draftFromIdentity = ref('')
const draftContactId = ref('')
const draftTo = ref('')
const draftSubject = ref('')
const draftBody = ref('')
const draftManualTo = ref('')
const contacts = ref([])
const contactsLoading = ref(false)
const showContactEditor = ref(false)
const editingContact = ref(null)
const bodyViewMode = ref('plain')
const hoveredAnnotatedBlockId = ref('')
let hoveredAnnotatedBlockClearTimer = null
const selectedKind = ref('')
const selectedReachout = ref(null)
const selectedSalesforceEmailId = ref('')
const selectedSalesforceEventId = ref('')
const selectedSignal = ref(null)
const selectedObjectType = ref('')
const selectedObjectId = ref('')

const reachoutId = computed(() => String(props.reachout?.id || '').trim())
const modalTitle = computed(() => {
  if (props.mode !== 'modal') return ''
  const type = String(reachoutData.value?.type || '').trim()
  if (type === 'reachout_call') return 'Review Call'
  if (type === 'reachout_linkedin_connect') return 'Review LinkedIn Connect'
  if (type === 'reachout_voicemail') return 'Review Voicemail'
  return 'Review Reachout'
})
const effectiveBodyMinHeight = computed(() => (props.mode === 'embedded' ? '' : props.bodyMinHeight))
const effectiveBodyMaxHeight = computed(() => (props.mode === 'embedded' ? '' : props.bodyMaxHeight))
const reachoutAccountId = computed(() => String(reachoutData.value?.account_id || '').trim())
const hasLinkedContact = computed(() => String(reachoutData.value?.contact_id || '').trim().length > 0)

const reachoutType = computed(() => String(reachoutData.value?.type || '').trim())
const isEmailType = computed(() => reachoutType.value === 'reachout_email')
const isCallType = computed(() => reachoutType.value === 'reachout_call')
const isLinkedinType = computed(() => reachoutType.value === 'reachout_linkedin_connect')
const isVoicemailType = computed(() => reachoutType.value === 'reachout_voicemail')
const isEmailReachout = computed(() => reachoutType.value === 'reachout_email')
const isManualType = computed(() => isCallType.value || isLinkedinType.value)

const status = computed(() => reachoutData.value?.status)
// lifecycle_state wins while the reachout is in flight, so the pill walks
// through scheduled → dialing without a refresh.
const pillStatus = computed(() => reachoutData.value?.lifecycle_state || status.value)
const isTestMode = computed(() => Boolean(reachoutData.value?.test_mode))
// "Post-sent" = the outreach attempt is genuinely complete. For voicemail this
// is the set of true-terminal statuses; ``passed_to_human_dialer`` is excluded
// because the SDR hasn't actually reached out to the prospect yet.
const VOICEMAIL_POST_SENT_STATUSES = new Set([
  'voicemail_left', 'human_picked_up', 'no_answer', 'busy',
  'dialer_failed', 'out_of_window', 'out_of_capacity',
  'call_returned',
  'connected_interested', 'connected_not_interested', 'connected_callback',
  'gatekeeper', 'wrong_number', 'do_not_call',
])
const isPostSent = computed(() => {
  if (isEmailType.value) return new Set(['sent', 'delivered', 'opened', 'bounced', 'spam', 'replied']).has(status.value)
  if (isVoicemailType.value) return VOICEMAIL_POST_SENT_STATUSES.has(status.value)
  return false
})

const isStarred = computed(() => !!reachoutData.value?.starred_at)

const activeRevisionRunId = computed(() => {
  // Prefer the pre-loaded field from the main reachout query (no extra fetch needed)
  if (reachoutData.value?.pending_revision_run_id) return reachoutData.value.pending_revision_run_id
  // Fallback to revision history if loaded
  const pending = revisionHistory.value.find((r) => r.status === 'pending')
  return pending?.agent_run_id || null
})

const hasRevisions = computed(() => {
  return (reachoutData.value?.revision_count || 0) > 0 || revisionHistory.value.length > 0
})

async function toggleStar() {
  const rid = reachoutData.value?.id
  if (!rid) return
  actionLoading.value = true
  try {
    const endpoint = isStarred.value ? 'unstar' : 'star'
    await apiFetch(`/reachouts/${rid}/${endpoint}`, { method: 'POST' })
    if (reachoutData.value) {
      reachoutData.value = {
        ...reachoutData.value,
        starred_at: isStarred.value ? null : new Date().toISOString(),
      }
      emit('updated', reachoutData.value)
    }
  } catch (e) {
    actionError.value = e?.message || 'Failed to update star'
  } finally {
    actionLoading.value = false
  }
}

// Ownership gate — the backend computes whether the requesting user can act
// on this reachout (based on owner_user_id) and returns it as user_can_act.
const userCanAct = computed(() => reachoutData.value?.user_can_act !== false)

const canEdit = computed(() => !isTestMode.value && (
  ((isEmailType.value || isVoicemailType.value) && new Set(['pending_approval', 'send_failed', 'needs_mailbox']).has(status.value)) ||
  (isManualType.value && status.value !== 'deleted')
))
// Narrower than canEdit, which also gates the inline editor: revision is only
// supported for reachouts awaiting approval. Manual-task drafts (LinkedIn
// connect, call) sit in "pending" and the revision API rejects them, so the
// button is disabled rather than failing on click.
const canRevise = computed(() => canEdit.value && (status.value === 'pending_approval' || status.value === 'needs_mailbox'))

// An email written before its owner had a mailbox: it can be read and deleted, and becomes
// ready to approve once they connect one.
const needsMailbox = computed(() => status.value === 'needs_mailbox')
const { connect: connectGmail, connecting: connectingGmail } = useGoogleWorkspaceIntegration()
const canApprove = computed(() => !isTestMode.value && (isEmailType.value || isVoicemailType.value) && new Set(['pending_approval', 'send_failed']).has(status.value))
// Backend-computed approval gate (e.g. a voicemail whose owner has "Allow
// voicemails" off can't be approved — it would never send). Disable the button
// and nudge, keeping the reachout pending until they fix it in Calling Settings.
const approvalBlocked = computed(() => reachoutData.value?.approvable === false)
const approvalBlockReason = computed(() => String(reachoutData.value?.approval_block_reason || ''))
const canUpdateStatus = computed(() => !isTestMode.value && isManualType.value && status.value !== 'deleted')
// Block deletion once the reachout has terminally fired. Email terminals +
// voicemail terminals (the call already happened — no point letting users
// delete the record of it). Intermediate voicemail states
// (passed_to_agent_dialer, passed_to_human_dialer) are intentionally
// deletable so users can cancel an in-flight enrollment, mirroring how
// queued_for_sending is deletable for email.
const canDelete = computed(() => !isTestMode.value && !new Set([
  'deleted', 'sending', 'sent', 'delivered', 'opened', 'bounced', 'spam', 'replied', 'completed',
  // Voicemail terminals
  'voicemail_left', 'human_picked_up', 'no_answer', 'busy',
  'dialer_failed', 'out_of_window', 'out_of_capacity',
  'call_returned',
  'connected_interested', 'connected_not_interested', 'connected_callback',
  'gatekeeper', 'wrong_number', 'do_not_call',
]).has(status.value))
const shouldShowFooter = computed(() => {
  if (props.mode === 'modal') return true
  return Boolean(actionError.value || isEditing.value || canEdit.value || canApprove.value || canUpdateStatus.value || canDelete.value)
})

const showUpdateStatusPicker = ref(false)
const selectedUpdateStatus = ref('')

const linkedinStatusOptions = [
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
]
const callStatusOptions = [
  { value: 'connected_positive', label: 'Connected — Positive' },
  { value: 'connected_neutral', label: 'Connected — Neutral' },
  { value: 'connected_negative', label: 'Connected — Negative' },
  { value: 'left_voicemail', label: 'Left Voicemail' },
  { value: 'no_answer', label: 'No Answer / Busy' },
  { value: 'bad_number', label: 'Bad / Wrong Number' },
  { value: 'not_in_service', label: 'Not in Service' },
  { value: 'gatekeeper', label: 'Gatekeeper' },
]
const updateStatusOptions = computed(() => isLinkedinType.value ? linkedinStatusOptions : callStatusOptions)

// Manual reachout payload helpers
const callPhoneNumbers = computed(() => {
  const payload = reachoutData.value?.payload
  if (!payload || typeof payload !== 'object') return []
  const nums = payload.phone_numbers
  return Array.isArray(nums) ? nums.filter(Boolean) : []
})
const callResearchSummary = computed(() => {
  const payload = reachoutData.value?.payload
  if (!payload || typeof payload !== 'object') return ''
  return String(payload.research_summary || '').trim()
})
const linkedinUrl = computed(() => {
  const payload = reachoutData.value?.payload
  const fromPayload = (payload && typeof payload === 'object') ? String(payload.linkedin_url || '').trim() : ''
  if (fromPayload && fromPayload !== 'unknown') return fromPayload
  return String(reachoutData.value?.to || '').trim()
})
const linkedinCharCount = computed(() => String(reachoutData.value?.body || '').length)

// Voicemail payload helpers
const voicemailVoiceName = computed(() => {
  const payload = reachoutData.value?.payload
  if (!payload || typeof payload !== 'object') return ''
  return String(payload.voice_name || '').trim()
})

const voicemailSynthesizing = ref(false)
let voicemailPreviewAudio = null

async function listenVoicemailPreview() {
  if (!reachoutData.value?.id) return
  voicemailSynthesizing.value = true
  actionError.value = ''
  try {
    const reachoutId = reachoutData.value.id
    const blob = await apiFetch(`/reachouts/${reachoutId}/preview-audio`, {
      method: 'POST',
      responseType: 'blob',
    })
    // The preview endpoint may have repaired a stale voice_id in the
    // reachout's payload. Re-fetch so the Voice label reflects what was
    // actually synthesized (and what will be sent).
    loadReachout(reachoutId)
    if (voicemailPreviewAudio) { voicemailPreviewAudio.pause(); voicemailPreviewAudio = null }
    const url = URL.createObjectURL(blob)
    const audio = new Audio(url)
    voicemailPreviewAudio = audio
    audio.onended = () => URL.revokeObjectURL(url)
    audio.play()
  } catch (e) {
    actionError.value = e?.message || 'Voicemail preview failed'
  } finally {
    voicemailSynthesizing.value = false
  }
}

const emailSettings = computed(() => reachoutData.value?.email_settings || null)

const senderIdentityOptions = computed(() => {
  const rows = Array.isArray(emailSettings.value?.sender_identities) ? emailSettings.value.sender_identities : []
  return rows.map((row) => String(row?.email || '').trim()).filter(Boolean)
})

const senderIdentitySelectOptions = computed(() => {
  const rows = Array.isArray(emailSettings.value?.sender_identities) ? emailSettings.value.sender_identities : []
  return rows
    .map((row) => {
      const email = String(row?.email || '').trim()
      if (!email) return null
      const fullName = String(row?.full_name || '').trim()
      return {
        value: email,
        label: fullName ? `${fullName} <${email}>` : email,
        domain: 'google.com',
      }
    })
    .filter(Boolean)
})

const fromProvider = computed(() => {
  const identity = String(reachoutData.value?.from_identity || '').trim()
  if (!identity) return null
  const rows = Array.isArray(emailSettings.value?.sender_identities) ? emailSettings.value.sender_identities : []
  const found = rows.find((row) => String(row?.email || '').trim().toLowerCase() === identity.toLowerCase())
  return String(found?.provider || 'gmail').toLowerCase()
})

const devModeOverrideEmail = computed(() => {
  const mode = String(emailSettings.value?.mode || '').trim().toLowerCase()
  if (mode !== 'development') return null
  const override = String(emailSettings.value?.development_to_override || '').trim()
  return override || null
})

const actualRecipient = computed(() => {
  if (isPostSent.value) return String(reachoutData.value?.actual_to || '').trim()
  return String(devModeOverrideEmail.value || '').trim()
})
const showDevOverride = computed(() => {
  if (!isPostSent.value) return Boolean(devModeOverrideEmail.value)
  const original = normalizedEmail(reachoutData.value?.to)
  const actual = normalizedEmail(actualRecipient.value)
  return Boolean(original && actual && original !== actual)
})
// Voicemail dev override
const voicemailSettings = computed(() => reachoutData.value?.voicemail_settings || null)

const devModeOverridePhone = computed(() => {
  const mode = String(voicemailSettings.value?.mode || '').trim().toLowerCase()
  if (mode !== 'development') return null
  const override = String(voicemailSettings.value?.development_to_override || '').trim()
  return override || null
})

const voicemailActualRecipient = computed(() => {
  if (isPostSent.value) return String(reachoutData.value?.actual_to || '').trim()
  return String(devModeOverridePhone.value || '').trim()
})
const showVoicemailDevOverride = computed(() => {
  if (!isVoicemailType.value) return false
  if (!isPostSent.value) return Boolean(devModeOverridePhone.value)
  const original = String(reachoutData.value?.to || '').trim()
  const actual = voicemailActualRecipient.value
  return Boolean(original && actual && original !== actual)
})

// Phone number being called: override (dev mode), actual_to (sent), or stored "to".
const voicemailDialedPhone = computed(() => {
  const override = voicemailActualRecipient.value
  if (override) return override
  return String(reachoutData.value?.to || '').trim()
})

// Verified caller IDs, loaded lazily when we need to label a dev-override phone.
const verifiedCallerIds = ref([])
const verifiedCallerIdsLoaded = ref(false)

async function loadVerifiedCallerIds() {
  if (verifiedCallerIdsLoaded.value) return
  verifiedCallerIdsLoaded.value = true
  try {
    const res = await apiFetch('/integrations/twilio/verified-caller-ids')
    verifiedCallerIds.value = Array.isArray(res?.verified_caller_ids) ? res.verified_caller_ids : []
  } catch {
    verifiedCallerIds.value = []
  }
}

watch(showVoicemailDevOverride, (shown) => {
  if (shown) void loadVerifiedCallerIds()
}, { immediate: true })

// Override number belongs to the verified caller, not the prospect — label it with the verified caller's friendly_name.
const voicemailDialedPhoneDisplay = computed(() => {
  const phone = voicemailDialedPhone.value
  if (!showVoicemailDevOverride.value) return phone
  const match = verifiedCallerIds.value.find((c) => String(c?.phone_number || '').trim() === phone)
  const name = String(match?.friendly_name || '').trim()
  if (name && phone) return `${name} <${phone}>`
  return phone
})

// Contact name for the voicemail "To" row (no phone — that's in the Phone row now).
const voicemailToName = computed(() => {
  const name = String(recipientContext.value?.name || '').trim()
  return name || '—'
})

const recipientContext = computed(() => reachoutData.value?.recipient_context || null)
const contactOptions = computed(() => {
  return (contacts.value || [])
    .map((contact) => {
      const id = String(contact?.id || '').trim()
      const email = String(contact?.email || '').trim()
      if (!id || !email) return null
      const name = String(contact?.name || contact?.display?.name || contact?.display?.full_name || '').trim()
      const title = String(contact?.title || contact?.display?.title || contact?.display?.job_title || '').trim()
      const secondary = title ? ` — ${title}` : ''
      return {
        value: id,
        label: name ? `${name} <${email}>${secondary}` : `${email}${secondary}`,
      }
    })
    .filter(Boolean)
})
const selectedDraftContact = computed(() => {
  const target = String(draftContactId.value || '').trim()
  if (!target) return null
  return (contacts.value || []).find((contact) => String(contact?.id || '').trim() === target) || null
})
const linkedRecipientContact = computed(() => {
  const target = String(recipientContext.value?.contact_id || reachoutData.value?.contact_id || '').trim()
  if (!target) return null
  return (contacts.value || []).find((contact) => String(contact?.id || '').trim() === target) || null
})
const agentPillLabel = computed(() => String(reachoutData.value?.agent_name || reachoutData.value?.source_label || '').trim())
const agentPillIconName = computed(() => String(reachoutData.value?.agent_icon_name || '').trim())
const agentPillIconStyle = computed(() => String(reachoutData.value?.agent_icon_style || '').trim())
const agentPillIconColor = computed(() => String(reachoutData.value?.agent_icon_color || '').trim())
const agentRunHref = computed(() => {
  const id = String(reachoutData.value?.created_by_agent_run_id || '').trim()
  return id ? `/agent-runs/${encodeURIComponent(id)}` : ''
})
const accountPillLabel = computed(() => {
  return String(reachoutData.value?.account_name || reachoutData.value?.account_domain || '').trim()
})
const accountHref = computed(() => {
  const id = String(reachoutData.value?.account_id || '').trim()
  return id ? `/accounts/${encodeURIComponent(id)}` : ''
})
const sequenceName = computed(() => String(reachoutData.value?.sequence_name || '').trim() || null)
const sequenceHref = computed(() => {
  const id = String(reachoutData.value?.sales_play_id || '').trim()
  return id ? `/sales-plays/${encodeURIComponent(id)}/sequence` : ''
})
const crmSync = computed(() => {
  if (reachoutData.value?.hubspot_engagement_id) {
    return {
      label: 'HubSpot',
      domain: 'hubspot.com',
      href: hubspotCallReviewUrl(reachoutData.value.hubspot_engagement_id),
    }
  }
  if (reachoutData.value?.salesforce_task_id) {
    return {
      label: 'Salesforce',
      domain: 'salesforce.com',
      href: salesforceTaskReviewUrl(reachoutData.value.salesforce_task_id),
    }
  }
  return null
})

const sequenceStepLabel = computed(() => {
  const index = reachoutData.value?.sequence_step_index
  if (typeof index !== 'number') return ''
  const total = reachoutData.value?.sequence_step_count
  return typeof total === 'number' ? `Step ${index + 1} of ${total}` : `Step ${index + 1}`
})
// Effective timezone for the send window (contact-specific, then tenant default).
// Used for both pre-send and post-send display.
const effectiveSendTimezone = computed(() => String(reachoutData.value?.effective_send_timezone || '').trim() || null)
const effectiveSendTimezoneSource = computed(() => {
  const src = String(reachoutData.value?.effective_send_timezone_source || '').trim()
  if (!src) return null
  if (src === 'tenant_default') return 'tenant default'
  if (src === 'tenant_us_default') return 'US default'
  return src
})
const tenantTimezoneSendingEnabled = computed(() => Boolean(reachoutData.value?.tenant_timezone_sending_enabled))
// Keep sendTimezone for backwards compat with any other references
const sendTimezone = effectiveSendTimezone

const timezoneBadgeMode = computed(() => {
  if (isPostSent.value) {
    return reachoutData.value?.sent_timezone_source === 'immediate' ? 'immediate' : 'scheduled'
  }
  return tenantTimezoneSendingEnabled.value ? 'scheduled' : 'immediate'
})

// Returns "now" if currently in business hours in the given IANA timezone,
// otherwise "in Xh Ym" until the next Mon–Fri 9 AM window.
function _nextSendWindowLabel(ianaTimezone) {
  try {
    const now = new Date()
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaTimezone,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(now)
    const dowStr = parts.find(p => p.type === 'weekday')?.value ?? ''
    const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(dowStr)
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value ?? '0', 10)
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value ?? '0', 10)
    if (dow >= 1 && dow <= 5 && hour >= 9 && hour < 17) return 'now'
    // Find ms until next business day 9:00 AM in target timezone
    // Walk forward day by day (max 7) until we land on a weekday
    const msPerMin = 60_000
    const msPerHour = 3_600_000
    const msPerDay = 86_400_000
    // Minutes remaining today after current time
    let candidate = new Date(now)
    // Advance to next 9 AM: subtract current time-of-day in tz, add 1 day, set to 9:00
    // Simpler: just try each upcoming day until we find Mon–Fri
    for (let daysAhead = 0; daysAhead <= 7; daysAhead++) {
      const tryDate = new Date(now.getTime() + daysAhead * msPerDay)
      const tryParts = new Intl.DateTimeFormat('en-US', {
        timeZone: ianaTimezone,
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      }).formatToParts(tryDate)
      const tryDow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(
        tryParts.find(p => p.type === 'weekday')?.value ?? ''
      )
      const tryHour = parseInt(tryParts.find(p => p.type === 'hour')?.value ?? '0', 10)
      const tryMin = parseInt(tryParts.find(p => p.type === 'minute')?.value ?? '0', 10)
      if (tryDow >= 1 && tryDow <= 5) {
        // This is a weekday — find ms to 9:00 AM on this day
        const minsUntil9 = (9 - tryHour) * 60 - tryMin
        if (daysAhead > 0 || minsUntil9 > 0) {
          const msUntil = daysAhead * msPerDay + minsUntil9 * msPerMin
          const totalMins = Math.round(msUntil / msPerMin)
          const h = Math.floor(totalMins / 60)
          const m = totalMins % 60
          if (h === 0) return `in ${m}m`
          if (m === 0) return `in ${h}h`
          return `in ${h}h ${m}m`
        }
      }
    }
    return ''
  } catch {
    return ''
  }
}

const timezoneBadgeLabel = computed(() => {
  if (isPostSent.value) {
    const sentSrc = reachoutData.value?.sent_timezone_source
    if (sentSrc === 'immediate') return 'Sent immediately'
    if (sentSrc === 'none') return 'Sent · no constraint'
    const tz = effectiveSendTimezone.value
    return tz ? `Sent · ${tz}` : 'Sent'
  }
  // Pre-send
  if (timezoneBadgeMode.value === 'immediate') return 'Send immediately'
  const tz = effectiveSendTimezone.value
  if (!tz) return 'Scheduled · no timezone · sends immediately'
  const timing = _nextSendWindowLabel(tz)
  return `Scheduled · ${tz}${timing ? ` · ${timing}` : ''}`
})

const sendTimezoneTitle = computed(() => {
  const src = reachoutData.value?.sent_timezone_source
  if (src === 'immediate') return 'Sent via the manual approval path — bypassed the timezone-aware scheduler'
  if (src === 'none') return 'Sent via scheduler with no timezone constraint (no business hours filter applied)'
  if (!isPostSent.value && timezoneBadgeMode.value === 'immediate') {
    return 'Timezone-aware sending is off for this play — sends immediately'
  }
  const tz = effectiveSendTimezone.value
  const tzSrc = effectiveSendTimezoneSource.value
  if (!tz) return 'No timezone constraint — sends at any time'
  const verb = isPostSent.value ? 'Sent' : 'Scheduled'
  return `${verb} during business hours (Mon–Fri 9am–5pm) in ${tz}${tzSrc ? ` · source: ${tzSrc}` : ''}`
})
const showMetaPills = computed(() => Boolean(pillStatus.value || agentRunHref.value || accountHref.value))
const annotatedBodyPayload = computed(() => {
  const payload = reachoutData.value?.annotated_body_json
  if (payload && typeof payload === 'object') return payload
  return { schema_version: 1, blocks: [] }
})
const annotatedBlocks = computed(() => {
  const blocks = annotatedBodyPayload.value?.blocks
  if (!Array.isArray(blocks)) return []
  return blocks.filter((block) => block && typeof block === 'object')
})
const hasAnnotatedBlocks = computed(() => annotatedBlocks.value.length > 0)
const replyQuotePlain = computed(() => {
  const raw = annotatedBodyPayload.value?._reply_quote_plain
  return typeof raw === 'string' ? raw.trim() : ''
})
const displayBody = computed(() => {
  const body = String(reachoutData.value?.body || '').trim()
  if (!replyQuotePlain.value) return body
  return body + '\n\n' + replyQuotePlain.value
})
const hoveredAnnotatedBlock = computed(() => {
  const hoveredId = String(hoveredAnnotatedBlockId.value || '').trim()
  if (hoveredId) {
    const found = annotatedBlocks.value.find((block) => String(block?.block_id || '').trim() === hoveredId)
    if (found) return found
  }
  return null
})

const fromDisplay = computed(() => {
  const identity = String(reachoutData.value?.from_identity || '').trim()
  const rows = Array.isArray(emailSettings.value?.sender_identities) ? emailSettings.value.sender_identities : []
  const found = rows.find((row) => String(row?.email || '').trim().toLowerCase() === identity.toLowerCase())
  const fullName = String(found?.full_name || '').trim()
  if (fullName && identity) return `${fullName} <${identity}>`
  return identity
})

const toPrimaryLine = computed(() => {
  const name = String(recipientContext.value?.name || '').trim()
  // Voicemail recipient address is the phone number (stored on reachout.to);
  // email recipient address is the email (prefer recipientContext.email which
  // is the contact's canonical email — falls back to the raw reachout.to).
  const address = isVoicemailType.value
    ? String(reachoutData.value?.to || '').trim()
    : String(recipientContext.value?.email || normalizedEmail(reachoutData.value?.to) || reachoutData.value?.to || '').trim()
  if (name && address) return `${name} <${address}>`
  return address || '—'
})

const toSecondaryLine = computed(() => {
  const title = String(recipientContext.value?.title || '').trim()
  if (title) return title
  return ''
})

const recipientLinks = computed(() => {
  const links = []
  const linkedinUrl = String(recipientContext.value?.linkedin_url || '').trim()
  const salesforceContactId = String(
    recipientContext.value?.salesforce_contact_id || linkedRecipientContact.value?.salesforce?.contact_id || ''
  ).trim()
  const salesforceUrl = salesforceContactId ? getSalesforceUrl({ Id: salesforceContactId }, 'Contact') : null
  if (linkedinUrl) {
    links.push({ label: 'Open LinkedIn profile', href: linkedinUrl, domain: 'linkedin.com' })
  }
  if (salesforceUrl) {
    links.push({ label: 'Open Salesforce contact', href: salesforceUrl, domain: 'salesforce.com' })
  }
  return links
})

function normalizedEmail(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const match = raw.match(/<\s*([^<>\s]+@[^<>\s]+)\s*>/)
  return (match ? match[1] : raw).trim().toLowerCase()
}

function senderIdentityLabel(email) {
  const rows = Array.isArray(emailSettings.value?.sender_identities) ? emailSettings.value.sender_identities : []
  const found = rows.find((row) => String(row?.email || '').trim().toLowerCase() === String(email || '').trim().toLowerCase())
  const fullName = String(found?.full_name || '').trim()
  return fullName ? `${fullName} <${email}>` : email
}

function syncDraft() {
  // Voicemail no longer collects a sender — only LinkedIn / email surfaces
  // need a default from_identity here.
  const defaultFromIdentity = isVoicemailType.value ? '' : String(senderIdentityOptions.value[0] || '')
  draftFromIdentity.value = String(reachoutData.value?.from_identity || defaultFromIdentity || '').trim()
  draftContactId.value = String(reachoutData.value?.contact_id || '').trim()
  draftTo.value = String(reachoutData.value?.to || '').trim()
  draftSubject.value = String(reachoutData.value?.subject || '').trim()
  draftBody.value = String(reachoutData.value?.body || '').trim()
  // For manual types and voicemail, initialize from the displayed value (not raw `to`)
  if (isCallType.value || isVoicemailType.value) {
    const phones = Array.isArray(reachoutData.value?.payload?.phone_numbers) ? reachoutData.value.payload.phone_numbers : []
    draftManualTo.value = String(phones[0] || reachoutData.value?.to || '').trim()
  } else if (isLinkedinType.value) {
    const fromPayload = String(reachoutData.value?.payload?.linkedin_url || '').trim()
    draftManualTo.value = (fromPayload && fromPayload !== 'unknown') ? fromPayload : String(reachoutData.value?.to || '').trim()
  } else {
    draftManualTo.value = String(reachoutData.value?.to || '').trim()
  }
  bodyViewMode.value = 'plain'
  hoveredAnnotatedBlockId.value = ''
}

async function loadContacts() {
  const accountId = String(reachoutAccountId.value || '').trim()
  contacts.value = []
  if (!accountId) return
  contactsLoading.value = true
  try {
    const resp = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/contacts/picker`)
    contacts.value = Array.isArray(resp?.contacts) ? resp.contacts : []
  } catch {
    contacts.value = []
  } finally {
    contactsLoading.value = false
  }
}

async function loadReachout(idValue) {
  const id = String(idValue || '').trim()
  const gen = ++loadGeneration
  error.value = ''
  actionError.value = ''
  reachoutData.value = props.reachout || null
  syncDraft()
  if (!id) return
  loading.value = true
  try {
    const resp = await apiFetch(`/reachouts/${encodeURIComponent(id)}/full-detail`)
    if (gen !== loadGeneration) return // stale — user already navigated away
    reachoutData.value = resp?.reachout || null
    contacts.value = []
    emailEvents.value = Array.isArray(resp?.email_events) ? coalesceEvents(resp.email_events) : []
    voicemailEvents.value = Array.isArray(resp?.voicemail_events) ? resp.voicemail_events : []
    syncDraft()
    isEditing.value = false
  } catch (e) {
    if (gen !== loadGeneration) return
    reachoutData.value = null
    error.value = e?.message || 'Failed to load reachout'
  } finally {
    if (gen === loadGeneration) loading.value = false
  }
}

async function loadEmailEvents() {
  const id = String(reachoutData.value?.id || '').trim()
  if (!id) return
  eventsLoading.value = true
  try {
    const resp = await apiFetch(`/reachouts/${encodeURIComponent(id)}/events`)
    emailEvents.value = Array.isArray(resp?.events) ? coalesceEvents(resp.events) : []
  } catch {
    emailEvents.value = []
  } finally {
    eventsLoading.value = false
  }
}

async function loadVoicemailEvents() {
  const id = String(reachoutData.value?.id || '').trim()
  if (!id) return
  eventsLoading.value = true
  try {
    const resp = await apiFetch(`/reachouts/${encodeURIComponent(id)}/voicemail-events`)
    voicemailEvents.value = Array.isArray(resp?.events) ? resp.events : []
  } catch {
    voicemailEvents.value = []
  } finally {
    eventsLoading.value = false
  }
}

const deliveryEvents = computed(() => (isVoicemailType.value ? voicemailEvents.value : emailEvents.value))
const deliveryEventsLabel = computed(() => (isVoicemailType.value ? 'Call events' : 'Delivery events'))
const showDeliveryEventsPill = computed(() =>
  isVoicemailType.value ? voicemailEvents.value.length > 0 : isPostSent.value,
)

function eventContext(ev) {
  if (isVoicemailType.value) return voicemailEventContext(ev)
  return ev.payload?.email || ev.payload?.to || ''
}

function voicemailEventContext(ev) {
  if (!ev) return ''
  const t = String(ev.event_type || '').trim().toLowerCase()
  const p = ev.payload || {}
  if ((t === 'voicemail_left' || t === 'human_picked_up') && p.call_duration != null) return `Duration: ${p.call_duration}s`
  if (t === 'human_call_ended') {
    const parts = []
    if (p.actor_name) parts.push(String(p.actor_name))
    if (p.duration_seconds != null) {
      const total = Number(p.duration_seconds) || 0
      const mm = Math.floor(total / 60)
      const ss = String(total % 60).padStart(2, '0')
      parts.push(`${mm}:${ss}`)
    }
    return parts.join(' · ')
  }
  if (t === 'dialer_failed' && p.error_class) return String(p.error_class)
  if (t === 'call_returned' && p.callback_from) return `From: ${p.callback_from}`
  return ''
}

function coalesceEvents(raw) {
  const out = []
  let sawSent = false
  for (const ev of raw || []) {
    if (['submitted', 'processed'].includes(String(ev?.event_type || ''))) {
      if (sawSent) continue
      sawSent = true
      out.push({ ...ev, event_type: 'sent' })
      continue
    }
    out.push(ev)
  }
  return out
}

async function beginEdit() {
  if (!canEdit.value) return
  syncDraft()
  if (!contacts.value.length) await loadContacts()
  isEditing.value = true
}

function discardEdits() {
  syncDraft()
  actionError.value = ''
  isEditing.value = false
}

function hoverAnnotatedBlock(block) {
  cancelHoveredAnnotatedBlockClear()
  if (!annotatedBlockDataPoints(block).length) {
    hoveredAnnotatedBlockId.value = ''
    return
  }
  hoveredAnnotatedBlockId.value = String(block?.block_id || '').trim()
}

function clearHoveredAnnotatedBlock(event) {
  if (isPointerStillWithinHoverRegion(event)) return
  scheduleHoveredAnnotatedBlockClear()
}

function scheduleHoveredAnnotatedBlockClear() {
  cancelHoveredAnnotatedBlockClear()
  hoveredAnnotatedBlockClearTimer = window.setTimeout(() => {
    hoveredAnnotatedBlockId.value = ''
    hoveredAnnotatedBlockClearTimer = null
  }, 120)
}

function cancelHoveredAnnotatedBlockClear() {
  if (hoveredAnnotatedBlockClearTimer != null) {
    window.clearTimeout(hoveredAnnotatedBlockClearTimer)
    hoveredAnnotatedBlockClearTimer = null
  }
}

function clearHoveredAnnotatedBlockImmediately() {
  cancelHoveredAnnotatedBlockClear()
  hoveredAnnotatedBlockId.value = ''
}

function isPointerStillWithinHoverRegion(event) {
  const currentTarget = event?.currentTarget
  const relatedTarget = event?.relatedTarget
  if (!(currentTarget instanceof Node) || !(relatedTarget instanceof Node)) return false
  return currentTarget.contains(relatedTarget)
}

function annotatedBlockDataPoints(block) {
  return Array.isArray(block?.data_points) ? block.data_points.filter((item) => item && typeof item === 'object') : []
}

function normalizeObjectType(item) {
  return String(item?.object_type || 'unknown').trim().toLowerCase()
}

function displayObjectId(item) {
  return String(item?.object_id || '—').trim() || '—'
}

function closeDetail() {
  selectedKind.value = ''
  selectedReachout.value = null
  selectedSalesforceEmailId.value = ''
  selectedSalesforceEventId.value = ''
  selectedSignal.value = null
  selectedObjectType.value = ''
  selectedObjectId.value = ''
}

function openCreateContact() {
  editingContact.value = null
  showContactEditor.value = true
}

async function openEditContact() {
  if (!selectedDraftContact.value) return
  const accountId = String(reachoutAccountId.value || '').trim()
  const contactId = String(selectedDraftContact.value.id || '').trim()
  if (accountId && contactId) {
    try {
      const full = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/contacts/${encodeURIComponent(contactId)}`)
      editingContact.value = full
    } catch {
      editingContact.value = selectedDraftContact.value
    }
  } else {
    editingContact.value = selectedDraftContact.value
  }
  showContactEditor.value = true
}

function closeContactEditor() {
  showContactEditor.value = false
  editingContact.value = null
}

async function handleContactSaved(saved) {
  const savedId = String(saved?.id || '').trim()
  await loadContacts()
  if (savedId) draftContactId.value = savedId
  closeContactEditor()
}

async function openDataPointDetail(item) {
  closeDetail()
  const objectType = normalizeObjectType(item)
  const objectId = displayObjectId(item)
  if (!objectId || objectId === '—') return

  if (objectType === 'reachout') {
    try {
      selectedReachout.value = await fetchReachout(objectId)
      selectedKind.value = 'reachout'
      return
    } catch {
    }
  }

  if (objectType === 'email' && reachoutAccountId.value) {
    selectedSalesforceEmailId.value = objectId
    selectedKind.value = 'salesforce_email'
    return
  }

  if (objectType === 'event' && reachoutAccountId.value) {
    selectedSalesforceEventId.value = objectId
    selectedKind.value = 'salesforce_event'
    return
  }

  if (objectType === 'signal') {
    try {
      selectedSignal.value = await apiFetch(`/signals/${encodeURIComponent(objectId)}`)
      selectedKind.value = 'signal'
      return
    } catch {
    }
  }

  selectedObjectType.value = objectType
  selectedObjectId.value = objectId
  selectedKind.value = 'object'
}

async function saveDraft() {
  if (!reachoutData.value?.id) return
  actionLoading.value = true
  actionError.value = ''
  try {
    let body
    if (isVoicemailType.value) {
      // No from_identity here — the dialer claims a pool number at dispatch
      // time, so the UI no longer collects a sender choice for voicemail.
      body = {
        to: String(draftManualTo.value || '').trim(),
        body: String(draftBody.value || '').trim(),
      }
    } else if (isManualType.value) {
      body = {
        to: String(draftManualTo.value || '').trim(),
        body: String(draftBody.value || '').trim(),
      }
    } else {
      body = {
        from_identity: String(draftFromIdentity.value || '').trim(),
        contact_id: hasLinkedContact.value ? (String(draftContactId.value || '').trim() || null) : undefined,
        to: hasLinkedContact.value ? undefined : String(draftTo.value || '').trim(),
        subject: String(draftSubject.value || '').trim(),
        body: String(draftBody.value || '').trim(),
      }
    }
    const updated = await apiFetch(`/reachouts/${encodeURIComponent(reachoutData.value.id)}`, {
      method: 'PUT',
      body,
    })
    reachoutData.value = updated || reachoutData.value
    syncDraft()
    isEditing.value = false
    emit('updated', updated || {})
  } catch (e) {
    actionError.value = e?.message || 'Failed to save reachout'
  } finally {
    actionLoading.value = false
  }
}

async function approve() {
  if (!reachoutData.value?.id || actionLoading.value) return

  actionLoading.value = true
  actionError.value = ''
  try {
    const resp = await apiFetch(`/reachouts/${encodeURIComponent(reachoutData.value.id)}/approve`, { method: 'POST' })
    if (resp?.status) reachoutData.value = { ...reachoutData.value, status: resp.status }
    emit('updated', { id: reachoutData.value.id, status: resp?.status })
    if (props.mode === 'modal') emit('close')
  } catch (e) {
    actionError.value = e?.message || 'Failed to approve reachout'
  } finally {
    actionLoading.value = false
  }
}

async function submitUpdateStatus() {
  if (!reachoutData.value?.id || !selectedUpdateStatus.value) return
  actionLoading.value = true
  actionError.value = ''
  try {
    const resp = await apiFetch(`/reachouts/${encodeURIComponent(reachoutData.value.id)}/update-status`, {
      method: 'POST',
      body: { status: selectedUpdateStatus.value },
    })
    if (resp?.status) reachoutData.value = { ...reachoutData.value, status: resp.status }
    emit('updated', { id: reachoutData.value.id, status: resp?.status })
    selectedUpdateStatus.value = ''
  } catch (e) {
    actionError.value = e?.message || 'Failed to update status'
  } finally {
    actionLoading.value = false
  }
}

function openDismissDialog() {
  dismissReason.value = ''
  dismissNotes.value = ''
  dismissDialogOpen.value = true
}

function closeDismissDialog() {
  dismissDialogOpen.value = false
}

async function confirmDismiss() {
  if (!reachoutData.value?.id || !dismissReason.value) return
  actionLoading.value = true
  actionError.value = ''
  try {
    const resp = await apiFetch(`/reachouts/${encodeURIComponent(reachoutData.value.id)}/dismiss`, {
      method: 'POST',
      body: { exit_reason: dismissReason.value, exit_notes: dismissNotes.value || null },
    })
    dismissDialogOpen.value = false
    if (resp?.reachout_id) reachoutData.value = { ...reachoutData.value, status: 'deleted' }
    emit('deleted', { id: reachoutData.value.id, status: 'deleted' })
    if (props.mode === 'modal') emit('close')
  } catch (e) {
    actionError.value = e?.message || 'Failed to delete reachout'
  } finally {
    actionLoading.value = false
  }
}

function formatDateTimeLong(ts) {
  if (!ts) return ''
  try {
    const d = new Date(ts)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleString()
  } catch {
    return ''
  }
}

watch(() => reachoutId.value, async (id) => {
  await loadReachout(id)
}, { immediate: true })

watch(() => props.reachout, (nextReachout) => {
  reachoutData.value = nextReachout || null
})

// Re-fetch contacts only when account changes mid-session while editing.
watch(() => reachoutAccountId.value, async (newId, oldId) => {
  if (isEditing.value && oldId && newId && newId !== oldId) await loadContacts()
})

watch(() => reachoutData.value?.status, async () => {
  if (isEmailType.value && isPostSent.value) await loadEmailEvents()
  if (isVoicemailType.value) await loadVoicemailEvents()
})

watch(() => reachoutData.value?.lifecycle_state, async () => {
  if (isVoicemailType.value) await loadVoicemailEvents()
})

watch(annotatedBlocks, (blocks) => {
  const hoveredId = String(hoveredAnnotatedBlockId.value || '').trim()
  if (hoveredId && !blocks.some((block) => String(block?.block_id || '').trim() === hoveredId)) {
    clearHoveredAnnotatedBlockImmediately()
  }
})

function onDialogEscape(e) {
  if (e.key !== 'Escape') return
  if (revisionDialogOpen.value) revisionDialogOpen.value = false
  else if (deliveryDialogOpen.value) deliveryDialogOpen.value = false
  else return
  e.stopPropagation()
}

watch(
  () => revisionDialogOpen.value || deliveryDialogOpen.value,
  (anyOpen) => {
    if (anyOpen) window.addEventListener('keydown', onDialogEscape, true)
    else window.removeEventListener('keydown', onDialogEscape, true)
  },
)

onMounted(() => { void preloadCrmLinkConfig() })

onBeforeUnmount(() => {
  cancelHoveredAnnotatedBlockClear()
  window.removeEventListener('keydown', onDialogEscape, true)
})
</script>

<style scoped>
.badgeMeta {
  opacity: 0.65;
  font-size: 11px;
}

.addressBlock {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border2);
  flex-shrink: 0;
}

.phoneLink {
  color: var(--primary);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
}
.phoneLink:hover { text-decoration: underline; }

.liLink {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--primary);
  text-decoration: none;
  font-size: 13px;
}
.liLink:hover { text-decoration: underline; }

.liMessagePanel {
  font-size: 14px;
  min-height: 80px;
}

.missingInfo {
  font-size: 13px;
  font-style: italic;
}

.charCounter {
  font-size: 11px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}
.charCounterWarn { color: var(--warning, #f59e0b); font-weight: 600; }
.approvalBlockNote { margin: 0; font-size: 12px; font-weight: 600; line-height: 1.3; color: var(--warning, #f59e0b); }

.updateStatusGroup {
  display: flex;
  align-items: center;
  gap: 6px;
}

.updateStatusSelect {
  width: 200px;
  flex-shrink: 0;
}

.btn.danger-outline {
  border: 1px solid var(--danger, #ef4444);
  color: var(--danger, #ef4444);
  background: transparent;
}
.btn.danger-outline:hover:not(:disabled) {
  background: color-mix(in srgb, var(--danger, #ef4444) 10%, transparent);
}

.messageBlock {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ── Bare subject / body (Gmail-like) ──────────────────────────────
   View and edit render the same shape — rules instead of field boxes — so
   toggling edit doesn't shift anything. The inputs inherit the display
   typography rather than looking like form controls; they reveal themselves on
   focus. Height comes from the flex chain, so no fixed rows. */
.bareSubject {
  font-family: inherit;
  font-weight: 600;
  font-size: 15px;
  line-height: 1.3;
  color: var(--text);
  flex-shrink: 0;
  /* Negative margin cancels the input padding so the editable text starts on the
     same pixel as the view-mode text. */
  margin: 0 -6px;
  padding: 2px 6px;
  border: none;
  border-radius: 6px;
  background: none;
  width: auto;
  outline: none;
}
input.bareSubject::placeholder { color: var(--muted); font-weight: 400; }

/* --border2 to match .addressBlock's divider above it; --border is noticeably
   darker and made the two rules look like different elements. */
.bareRule {
  height: 1px;
  background: var(--border2);
  flex-shrink: 0;
}

.bareBody {
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: var(--text);
  white-space: pre-wrap;
  margin: 0 -6px;
  padding: 2px 6px;
  border: none;
  background: none;
  outline: none;
  resize: none;
}
textarea.bareBody::placeholder { color: var(--muted); }

.bodyModeBar {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.modeBtn {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.modeBtn.active {
  background: var(--badge-bg);
  color: var(--text);
}

.annotatedLayout {
  display: block;
  min-height: 0;
}

.annotatedLayout.embedded {
  flex: 1;
}

.annotatedBodyPanel {
  min-height: 0;
  overflow: visible;
  position: relative;
}

.annotatedInlineWrap {
  position: relative;
}

.annotatedInlineBlock {
  transition: text-decoration-color 120ms ease, background-color 120ms ease;
  border-radius: 3px;
}

.annotatedInlineBlock.interactive {
  cursor: pointer;
  position: relative;
  text-decoration-line: underline;
  text-decoration-style: dashed;
  text-decoration-color: #60a5fa;
  text-decoration-thickness: 1.5px;
  text-underline-offset: 5px;
}

.annotatedInlineBlock.hovered {
  text-decoration-style: solid;
  text-decoration-color: #2563eb;
  text-decoration-thickness: 1.5px;
  text-underline-offset: 5px;
  background: rgba(96, 165, 250, 0.14);
}

.annotatedHovercard {
  position: absolute;
  left: 0;
  top: auto;
  bottom: calc(100% - 2px);
  z-index: 40;
  width: min(320px, calc(100vw - 64px));
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.16);
  white-space: normal;
}

.annotatedHovercardTitle {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.fieldRow {
  display: grid;
  grid-template-columns: 47px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
}

.fieldKey {
  font-size: 12px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.fieldRowOverride {
  align-items: center;
}

.fieldKeyOverride {
  color: #a855f7;
}

.iconLink {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--surface);
}

/* Contact picker: dropdown plus two icon actions on one line. These were
   full-width labelled buttons on their own row, which cost a whole row of
   height in a card that has to fit the viewport. */
.contactPickerRow {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.contactPickerSelect { flex: 1 1 auto; min-width: 0; }
.iconBtn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Matches .input so the buttons line up with the dropdown. */
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--field-bg);
  color: var(--muted);
  cursor: pointer;
}
.iconBtn:hover:not(:disabled) {
  color: var(--text);
  border-color: var(--focus-border, var(--border));
}
.iconBtn:disabled { opacity: 0.45; cursor: default; }

.toInlineBlock {
  display: inline-flex;
  align-items: center;
  column-gap: 8px;
  min-width: 0;
  max-width: 100%;
}

.toPrimaryText {
  font-weight: 600;
  line-height: 1.4;
  min-width: 0;
  flex: 0 0 auto;
  white-space: nowrap;
}

.toSecondaryText {
  font-size: 13px;
  line-height: 1.25;
  min-width: 0;
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.toLinkIcons {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  flex: 0 0 auto;
}

.toOverrideLine {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  color: #a855f7;
}

.toOverrideText {
  color: inherit;
}

.toOverrideLabel {
  color: inherit;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
}

.integrationIcon {
  width: 16px;
  height: 16px;
  border-radius: 4px;
}

@media (max-width: 900px) {
  .annotatedHovercard {
    width: min(280px, calc(100vw - 48px));
  }
}

/* Dismiss dialog */
.dismissOverlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dismissDialog {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  width: 380px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
}

.dismissHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;
  border-bottom: 1px solid var(--border);
}

.dismissTitle {
  font-weight: 700;
  font-size: 14px;
  color: var(--text);
}

.dismissClose {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--muted);
  font-size: 14px;
  padding: 2px 4px;
  line-height: 1;
}

.dismissBody {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dismissHint {
  margin: 0 0 6px;
  font-size: 13px;
  color: var(--muted);
}

.dismissOption {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text);
  cursor: pointer;
  padding: 4px 0;
}

.dismissOption input[type="radio"] {
  cursor: pointer;
  flex: none;
}

.dismissNotes {
  margin-top: 8px;
  width: 100%;
  resize: vertical;
  font-size: 13px;
}

.dismissFooter {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--border);
}

.revisionBanner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--selection-bg);
  border: 1px solid var(--selection-border);
  border-radius: 8px;
  font-size: 13px;
  flex-shrink: 0;
}

.revisionBanner .spinner {
  width: 14px;
  height: 14px;
  border: 2px solid var(--border);
  border-top-color: #8b5cf6;
  border-radius: 50%;
  animation: revSpin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes revSpin {
  to { transform: rotate(360deg); }
}

.revisionRunLink {
  margin-left: auto;
  color: var(--primary);
  font-size: 12px;
  text-decoration: none;
}

.revisionRunLink:hover {
  text-decoration: underline;
}

.revisionPill {
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.revisionPill:hover {
  color: var(--text);
}

.pillCount {
  font-weight: 700;
}

.revisionDialog {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  width: 900px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 96px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
}

.revisionDialogBody {
  padding: 14px 16px;
  overflow-y: auto;
}

.deliveryDialog {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  width: 520px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 96px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
}

.eventList {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.eventRow {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  font-size: 13px;
}

.revisionEntry {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--border2);
}

.revisionEntry:last-child {
  padding-bottom: 0;
  margin-bottom: 0;
  border-bottom: none;
}

.revisionEntryHeader {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--muted);
}

.revisionInstr {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.revertRow {
  display: flex;
  justify-content: flex-end;
}

.revisionError {
  font-size: 12px;
  color: var(--danger);
  margin-top: 4px;
}

.revisionAborted {
  font-size: 12px;
  color: var(--muted);
  margin-top: 4px;
}


</style>
